<template>
  <component
    :is="$register ? 'q-scroll-area': 'div'"
    :class="$register ? '' :'page q-pb-xl'"
    data-test-id="movie-page"
    v-bind="$attrs"
  >
    <component
      :is="heroComponent"
      :movie-id="$register ? movieId : undefined"
      :common-showing-badges="commonShowingBadges"
      @sign-in-requested="() => $emit('sign-in-requested')"
      @clicked-trailer="!$register && (trailer = true); !$register && $trackEvent($customerEvents.MOVIE_PAGE_CLICKED_POSTER, { movieId: findMovieBySlug.id, movieName: findMovieBySlug.name })"
      @unselect-movie="() => $emit('unselect-movie')"
    />
    <div
      v-if="$q.screen.lt.sm"
      class="row items-stretch q-px-md justify-center container"
    >
      <movie-heading
        :movie-id="movieId"
        :outline="false"
        color="grey-4"
        text-color="dark"
        :rated="findMovieBySlug.rating"
        :rating-reason="findMovieBySlug.ratingReason"
        size="xl"
        text-size="h4"
        :text-color-class="$q.dark.isActive ? 'text-white' : 'text-dark'"
        text-center
        dense
      />
      <q-btn-toggle
        v-model="tab"
        spread
        no-caps
        toggle-color="responsive"
        toggle-text-color="responsive"
        color="responsive-grey-4"
        text-color="responsive"
        class="full-width q-my-md no-box-shadow border-responsive-grey-3 rounded-borders"
        :options="[
          { label: $t('movie.overview'), value: 'overview' },
          { label: $t('time.actions.selectDateAndTime'), value: 'showtimes' }
        ]"
      />
      <template v-if="tab === 'overview'">
        <movie-overview
          :movie-id="movieId"
        />
      </template>
      <template v-if="tab === 'showtimes'">
        <showtimes-list
          class="q-pa-none full-width"
          :initial-movie-id="multipleSiteMovieIds ? findMovieBySlug.id.toString() : null"
          :show-title="false"
          :showtimes-list-button-type="currentSite.moviePageConfig.buttonType"
          :show-button-progress-bar="currentSite.moviePageConfig.showButtonProgressBar"
          :border-radius="currentSite.moviePageConfig.buttonBorderRadius"
          is-movie-page
          :is-mobile-view="$q.screen.lt.sm"
          :initial-movie-ids="multipleSiteMovieIds"
          :initial-site-ids="movieQuerySiteIds"
          :is-showtimes-dark-mode="$q.dark.isActive"
          :use-current-location="useCurrentLocation"
          @select-showing="selectShowing($event)"
          @common-showing-badges="val => commonShowingBadges = val"
          @select-order="order => $emit('select-order', order)"
          @show-site-selector-drawer="$emit('show-site-selector-drawer')"
        />
      </template>
    </div>

    <showtimes-list
      v-if="!$q.screen.lt.sm"
      :override-showtime-view="SHOWTIME_LIST_VIEW_POS_OPTIONS.SHOWTIMES_BY_FILM"
      :initial-movie-id="multipleSiteMovieIds ? findMovieBySlug.id.toString() : null"
      :showtimes-list-button-type="currentSite.moviePageConfig.buttonType"
      :show-button-progress-bar="currentSite.moviePageConfig.showButtonProgressBar"
      :border-radius="currentSite.moviePageConfig.buttonBorderRadius"
      is-movie-page
      :is-mobile-view="$q.screen.lt.sm"
      :is-showtimes-dark-mode="$q.dark.isActive"
      :initial-movie-ids="multipleSiteMovieIds"
      :initial-site-ids="siteIds"
      :closest-locations-ids="closestLocationsIds"
      @select-showing="selectShowing($event)"
      @common-showing-badges="val => commonShowingBadges = val"
      @select-order="order => $emit('select-order', order)"
      @select-sites="(siteIds, getClosestLocations) => selectSites(siteIds, getClosestLocations)"
      @show-site-selector-drawer="$emit('show-site-selector-drawer')"
    />

    <q-dialog
      v-if="!$register"
      v-model="trailer"
      persistent
      :maximized="maximizedToggle"
      transition-show="slide-up"
      transition-hide="slide-down"
    >
      <q-card
        class="bg-dark text-white"
        :class="[$q.screen.lt.lg ? '' : 'q-mx-xxl q-my-xs']"
        :style="$q.screen.lt.sm ? '' : 'height: auto;'"
      >
        <q-bar>
          <q-space />

          <q-btn
            v-close-popup
            icon="close"
            flat
            round
            dense
          />
        </q-bar>
        <q-resize-observer
          debounce="500"
          @resize="setVideoStyle"
        />

        <q-video
          :style="{ 'top': $q.screen.lt.sm ? '20%' : '' }"
          :ratio="16/9"
          :src="'https://www.youtube.com/embed/' + findMovieBySlug.trailerYoutubeId + youtubeEmbeddedParams"
        />
      </q-card>
    </q-dialog>
  </component>
  <seo-h1
    v-if="computedSeoTitle"
    :title="computedSeoTitle"
  />
</template>

<script>
import { Cookies, date } from 'quasar'
import { metaMixin } from 'src/modules/consumer/components/mixins/MetaMixin'
import SeoH1 from 'src/modules/consumer/components/SeoH1'

const { formatDate } = date

import { HEADER_TYPES, SHOWTIME_LIST_VIEW_POS_OPTIONS } from 'src/models/site/Site'
import MovieSlugMixin from 'src/modules/consumer/components/mixins/MovieSlugMixin'
import MovieHeading from 'src/modules/consumer/components/MovieHeading'
import MovieOverview from 'src/modules/consumer/components/MovieOverview'
import ShowtimesList from 'src/modules/consumer/components/ShowtimesList'
import { defineAsyncComponent } from 'vue'
const VIEWPORT_RATIO_16_BY_9_AS_PERCENTAGE = (9 / 16) * 100

export default {
  name: 'MoviePage',
  components: {
    ShowtimesList,
    MovieOverview,
    MovieHeading,
    SeoH1,
    BannerMovieHero: defineAsyncComponent(() => import('src/modules/consumer/components/BannerMovieHero')),
    PosterMovieHero: defineAsyncComponent(() => import('src/modules/consumer/components/PosterMovieHero')),
    RegisterMovieHero: defineAsyncComponent(() => import('src/modules/register/components/RegisterMovieHero'))
  },
  configuration: ['currentSite', 'sites'],
  context: ['currentUser'],
  mixins: [
    metaMixin,
    MovieSlugMixin
  ],
  props: {
    movieId: {
      type: Number,
      required: false,
      default: null
    }
  },
  emits: ['unselect-movie', 'sign-in-requested', 'select-order', 'show-site-selector-drawer', 'select-showing'],
  data () {
    return {
      trailer: false,
      youtubeEmbeddedParams: '',
      maximizedToggle: true,
      videoStyle: '',
      commonShowingBadges: [],
      tab: 'showtimes',
      readMore: true,
      filteredMoviePageSiteIds: [],
      useCurrentLocation: false,
      closestLocationsIds: []
    }
  },
  models: {
    findMoviesBySlug: {
      fields: ['id'],
      variables () {
        return {
          urlSlug: this.computedMovieSlug,
          siteIds: this.siteIds,
          skip: !this.computedMovieSlug || !this.movieQuerySiteIds?.length
        }
      },
      immediate: true
    }
  },
  computed: {
    pageTitle () {
      return this.findMovieBySlug?.name
    },
    pageDescription () {
      return `${this.findMovieBySlug?.synopsis}${!this.findMovieBySlug?.starring ? '' : `\n\nStarring ${this.findMovieBySlug.starring}`}`
    },
    loggedIn () {
      return this.currentUser && this.currentUser.isRemote
    },
    multipleSiteMovieIds () {
      return _.map(this.findMoviesBySlug?.data, 'id')
    },
    heroComponent () {
      if (this.$register || this.$kiosk) {
        return 'RegisterMovieHero'
      } else if (this.currentSite.moviePageConfig.hidePosters) {
        return 'BannerMovieHero'
      } else {
        return 'PosterMovieHero'
      }
    },
    siteIds () {
      if (this.multipleSiteMovieIds && this.currentSite.consumerConfig.showSiteSelectorOnMoviePage) {
        return this.filteredMoviePageSiteIds || this.movieQuerySiteIds
      }
      return this.movieQuerySiteIds
    },
    computedSeoTitle () {
      return (this.pageTitle) + ' - ' + this.currentSite?.displayName
    }
  },
  created () {
    this.trailer = !this.$register && (this.$route.query.autoshow === 'true' || this.$route.query.autoplay === 'true' || this.trailer)
    this.youtubeEmbeddedParams = this.$route.query.autoplay === 'true' ? '?autoplay=1&mute=1' : this.youtubeEmbeddedParams
    _.extend(this, {
      formatDate,
      SHOWTIME_LIST_VIEW_POS_OPTIONS,
      HEADER_TYPES
    })
    this.loadPreferredLocations()
  },
  methods: {
    loadPreferredLocations () {
      if (this.multipleSiteMovieIds) {
        const preferredLocations = Cookies.get(this.$constants.cookies.preferredLocations)

        if (preferredLocations && preferredLocations.siteId === this.currentSite.id) {
          const preferredLocationsIds = preferredLocations.preferredLocationsIds
          this.filteredMoviePageSiteIds = preferredLocationsIds
        }
      }
    },
    setMovieId (movieId) {
      this.movieId = movieId
    },
    selectShowing (showing) {
      if (this.$order) {
        this.$order.currentShowing = showing
      }
      if (this.$register) {
        this.$emit('select-showing', showing)
      } else if (this.multipleSiteMovieIds && showing.movie.siteId !== this.currentSite.id) {
        window.location = showing.checkoutUrl
      } else {
        this.$trackEvent(this.$customerEvents.CLICKED_SHOWING, {
          showing_id: showing.id,
          showing_time: this.$d(showing.siteTime, 'shortLong'),
          ...this.findMovieBySlug?.trackingData
        })
        this.$router.push(showing.checkoutPath)
      }
    },
    setVideoStyle () {
      const ratio = ((window.innerHeight / window.innerWidth) * 100).toFixed(2)
      this.videoStyle = ratio < VIEWPORT_RATIO_16_BY_9_AS_PERCENTAGE ? `padding-bottom: ${ratio}%` : ''
    },
    selectSites (siteIds, getClosestLocations) {
      this.filteredMoviePageSiteIds = siteIds
      this.useCurrentLocation = getClosestLocations
      this.closestLocationsIds = getClosestLocations ? siteIds : []

      const cookieType = getClosestLocations
        ? this.$constants.cookies.type.useCurrentLocation
        : null

      const expiresInDays = getClosestLocations ? 1 : 5

      try {
        Cookies.set(this.$constants.cookies.preferredLocations, {
          siteId: this.currentSite.id,
          preferredLocationsIds: siteIds,
          type: cookieType,
          expires: expiresInDays
        })
      } catch (error) {
        console.warn('Failed to save preferred locations to cookies:', error)
      }
    }
  },
  async trackPage () {
    if (this.computedMovieSlug) {
      await this.findMovieBySlug.ensureLoaded()
    }
    return this.findMovieBySlug?.trackingData
  }
}
</script>

<style lang="scss" scoped>
.page {
  width: 100%;
  margin: -10px auto 0;
}
</style>
